import {
  ConsentScope,
  FundingFulfillmentMode,
  FundingKind,
  ReferralPayloadSchema,
  ResourceBookingSchema,
  ResourceSchema,
} from '../data/types';

export const REFERRAL_CONSENT_VERSION = '2026-09-01';

export const DEFAULT_CONSENT_SCOPES: ConsentScope[] = [
  'identity',
  'business_profile',
  'qualification',
];

export const CONSENT_SCOPE_LABELS: Record<ConsentScope, string> = {
  identity: 'Name and email',
  business_profile: 'Business name, type, and location',
  qualification: 'Whether you currently generate sales',
  operational_signals: 'Kitchen booking and usage summaries',
  menu: 'Master menu and recipes',
  supplies: 'Ingredient and supplier costs',
  sales: 'POS and channel sales transactions',
};

export const SHARE_CONSENT_SCOPES: ConsentScope[] = ['menu', 'supplies', 'sales'];
export const SHARE_CONSENT_VERSION = '2026-09-11';

export const FINANCIAL_WHEEL_LABELS: Record<string, string> = {
  accounting: 'Airibook accounting AI',
  margin_by_dish: 'Margin by Dish',
  inventory: 'Inventory Management and Sourcing',
  loyalty: 'AiriHub Diner Insights',
  demand: 'Airitime demand forecasting',
};

type QualificationInput = {
  commercially_active?: boolean
  business_stage?: string
  requires_active_sales?: boolean
  excluded_stages?: string[]
}

type QualificationResult = {
  qualified: boolean
  reason: string
}

/** Rule-based ICP check for financial-intelligence listings. */
export const qualifyFinancialResource = (input: QualificationInput): QualificationResult => {
  const excluded = (input.excluded_stages || []).map((stage) => stage.toLowerCase());
  const stage = (input.business_stage || '').toLowerCase();
  if (input.requires_active_sales !== false && input.commercially_active === false) {
    return {
      qualified: false,
      reason: 'This partner is for commercially active food businesses with current sales.',
    };
  }
  if (stage && excluded.includes(stage)) {
    return {
      qualified: false,
      reason: 'This partner is not a fit at the idea / pre-launch stage yet.',
    };
  }
  return {
    qualified: true,
    reason: 'This business looks like a fit for financial operating intelligence.',
  };
};

export const getFundingKind = (resource?: ResourceSchema | null): FundingKind => {
  return resource?.funding_profile?.kind || 'capital';
};

export const isFinancialIntelligenceResource = (resource?: ResourceSchema | null): boolean => {
  return getFundingKind(resource) === 'financial_intelligence';
};

export const getFundingFulfillmentMode = (resource?: ResourceSchema | null): FundingFulfillmentMode => {
  return resource?.funding_profile?.fulfillment_mode || 'application';
};

export const usesReferralHandoff = (resource?: ResourceSchema | null): boolean => {
  const mode = getFundingFulfillmentMode(resource);
  return isFinancialIntelligenceResource(resource)
    && (mode === 'referral_handoff' || mode === 'api_handoff' || mode === 'external_trial');
};

export const getPartnerDisplayName = (resource?: ResourceSchema | null): string => {
  return resource?.funding_profile?.partner?.display_name || resource?.name || 'this partner';
};

/**
 * Attach a referral code to the partner signup URL, replacing an existing `ref=` when present.
 */
export const buildPartnerSignupUrl = (signupUrl: string | undefined, referralCode: string): string => {
  const base = signupUrl || 'https://airiwheels.ai/';
  try {
    const url = new URL(base);
    url.searchParams.set('ref', referralCode);
    return url.toString();
  } catch {
    const withoutRef = base.replace(/([?&])ref=[^&]*/g, '').replace(/[?&]$/, '');
    const separator = withoutRef.includes('?') ? '&' : '?';
    return `${withoutRef}${separator}ref=${encodeURIComponent(referralCode)}`;
  }
};

/** Return a non-attributed partner information URL with query and hash data removed. */
export const getPartnerLearnMoreUrl = (signupUrl?: string): string => {
  try {
    const url = new URL(signupUrl || 'https://airiwheels.ai/');
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return 'https://airiwheels.ai/';
  }
};

/** Keep consented referral results visible instead of navigating to bookings. */
export const shouldNavigateAfterBooking = (booking: Pick<ResourceBookingSchema, 'referral_id'>): boolean => {
  return !booking.referral_id;
};

const REFERRAL_HANDOFF_FIELDS = [
  'commercially_active',
  'business_id',
  'business_name',
  'business_type',
  'business_stage',
  'city',
  'county',
  'state',
  'zipcode',
  'name',
  'email',
  'channel',
  'seminar_id',
  'reason',
] as const;

/** Build the outbound request from a strict Phase 0–1 allowlist. */
export const buildConsentedReferralRequest = (input: Record<string, unknown>): Record<string, unknown> => {
  const request: Record<string, unknown> = {
    consent: {
      granted: input.consent === true,
      scope: DEFAULT_CONSENT_SCOPES,
      version: REFERRAL_CONSENT_VERSION,
    },
  };
  REFERRAL_HANDOFF_FIELDS.forEach((field) => {
    if (input[field] !== undefined && input[field] !== '') request[field] = input[field];
  });
  return request;
};

export const filterConsentedPayload = (
  payload: ReferralPayloadSchema,
  scope: ConsentScope[] = DEFAULT_CONSENT_SCOPES
): ReferralPayloadSchema => {
  const allowed = new Set(scope);
  const filtered: ReferralPayloadSchema = {};
  if (allowed.has('identity')) {
    filtered.name = payload.name;
    filtered.email = payload.email;
  }
  if (allowed.has('business_profile')) {
    filtered.business_id = payload.business_id;
    filtered.business_name = payload.business_name;
    filtered.city = payload.city;
    filtered.county = payload.county;
    filtered.state = payload.state;
    filtered.zipcode = payload.zipcode;
    filtered.business_type = payload.business_type;
    filtered.business_stage = payload.business_stage;
  }
  if (allowed.has('qualification')) {
    filtered.commercially_active = payload.commercially_active;
    filtered.reason = payload.reason;
    filtered.next_action = payload.next_action;
  }
  return filtered;
};
